import { useEffect, useState, useRef } from 'react';

interface ScrollState {
  isSticky: boolean;
  hideHeader: boolean;
  scrollDirection: 'up' | 'down' | 'idle';
  scrollPosition: number;
}

interface UseNGSearchFormScrollOptions {
  enabled: boolean; // Only enabled when isNextGenHP is false
  gapBetweenHeaderAndForm?: number; // Default to 24px as mentioned in requirements
  searchFormPositionStyle?: 'absolute' | 'relative'; // Default to relative as mentioned in requirements
}

interface UseNGSearchFormScrollReturn {
  scrollState: ScrollState;
  searchFormRef: (el: HTMLDivElement | null) => void;
  headerElement: HTMLElement | null;
}

/**
 * Custom hook for controlling NGSearchForm scrolling behavior
 *
 * Automatically detects and hides header elements on scroll:
 * - Element with class="header"
 * - Element with data-component-name="brand_bar"
 * - Element with data-component-name="o-book-HotelHeader-Loading"
 *
 * Rules:
 * 1. Form becomes sticky only when the header is completely hidden (scroll > headerHeight)
 * 2. Downward scroll: when header is hidden, brandbar and hotelheader are also hidden, form is sticky at the top
 * 3. Upward scroll: form remains sticky until header becomes visible again
 * 4. Initial positioning: form is positioned headerHeight + 24px from top
 *
 * @param options - Configuration options
 * @returns Hook state and refs including dynamically calculated initialTopPosition
 */
export const useNGSearchFormScroll = (options: UseNGSearchFormScrollOptions): UseNGSearchFormScrollReturn => {
  const { enabled = true, gapBetweenHeaderAndForm = 24, searchFormPositionStyle = 'relative' } = options;

  const [scrollState, setScrollState] = useState<ScrollState>({
    isSticky: false,
    hideHeader: false,
    scrollDirection: 'idle',
    scrollPosition: 0,
  });

  const searchFormElementRef = useRef<HTMLDivElement | null>(null);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  // Use refs for DOM elements that we'll mutate, not state
  const headerElementRef = useRef<HTMLElement | null>(null);
  const brandBarElementRef = useRef<HTMLElement | null>(null);
  const hotelHeaderElementRef = useRef<HTMLElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(50); // Default fallback - only main header
  const formInitialOffsetTop = useRef<number | null>(null);

  // Calculate initial top position: headerHeight + 24px gap
  const initialTopPosition = searchFormPositionStyle === 'absolute' ? headerHeight : 0 + gapBetweenHeaderAndForm;

  // Callback ref for the search form element
  const searchFormRef = (el: HTMLDivElement | null) => {
    searchFormElementRef.current = el;
  };

  // Find and cache header elements, calculate only main header height
  useEffect(() => {
    if (!enabled) return;

    // Function to find and set all header elements
    const findAndSetHeaderElements = () => {
      // Find header element with class="header"
      const header = document.querySelector('.header') as HTMLElement;

      // Find brand bar element with data-component-name="brand_bar"
      const brandBar = document.querySelector('[data-component-name="brand_bar"]') as HTMLElement;

      // Find hotel header element with data-component-name="o-book-HotelHeader-Loading"
      const hotelHeader = document.querySelector('[data-component-name="o-book-HotelHeader-Loading"]') as HTMLElement;

      headerElementRef.current = header;
      brandBarElementRef.current = brandBar;
      hotelHeaderElementRef.current = hotelHeader;

      return { header };
    };

    // Calculate only the main header height (brandbar and hotelheader will just hide along with it)
    const calculateHeaderHeight = () => {
      const { header } = findAndSetHeaderElements();
      let totalHeight = 0;

      if (header) {
        const headerRect = header.getBoundingClientRect();
        totalHeight = headerRect.height || 0;
      }

      // Use fallback if header not found
      const newHeight = totalHeight > 0 ? totalHeight : 50;
      setHeaderHeight(newHeight);

      return { header };
    };

    // Initial calculation
    const { header } = calculateHeaderHeight();

    // Update header height on window resize
    window.addEventListener('resize', calculateHeaderHeight);

    // Use ResizeObserver to watch for main header size changes if available
    const resizeObservers: ResizeObserver[] = [];
    if (window.ResizeObserver && header) {
      const headerResizeObserver = new ResizeObserver(calculateHeaderHeight);
      headerResizeObserver.observe(header);
      resizeObservers.push(headerResizeObserver);
    }

    // Use MutationObserver to watch for DOM changes and re-detect header elements
    let mutationObserver: MutationObserver | null = null;
    if (window.MutationObserver) {
      mutationObserver = new MutationObserver(mutations => {
        let shouldRecalculate = false;

        mutations.forEach(mutation => {
          // Check if nodes were added
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                // Check if the added element or its children contain our target elements
                const hasTargetElement =
                  (typeof element.matches === 'function' &&
                    (element.matches('.header') ||
                      element.matches('[data-component-name="brand_bar"]') ||
                      element.matches('[data-component-name="o-book-HotelHeader-Loading"]'))) ||
                  (typeof element.querySelector === 'function' &&
                    (element.querySelector('.header') !== null ||
                      element.querySelector('[data-component-name="brand_bar"]') !== null ||
                      element.querySelector('[data-component-name="o-book-HotelHeader-Loading"]') !== null));

                if (hasTargetElement) {
                  shouldRecalculate = true;
                }
              }
            });
          }
        });

        if (shouldRecalculate) {
          // Use a small delay to ensure the elements are fully rendered
          setTimeout(calculateHeaderHeight, 100);
        }
      });

      // Start observing the document body for changes
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      window.removeEventListener('resize', calculateHeaderHeight);
      resizeObservers.forEach(observer => observer.disconnect());
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
    };
  }, [enabled]);

  // Helper to check if mobile search form modal is open
  const isMobileModalOpen = () => {
    const mobileSearchForm = document.querySelector('.mobile-search-form');
    return mobileSearchForm !== null && (mobileSearchForm as HTMLElement).offsetParent !== null;
  };

  // Watch for modal open/close and reset styles accordingly
  useEffect(() => {
    if (!enabled) return;

    let modalObserver: MutationObserver | null = null;

    const checkAndResetStyles = () => {
      if (searchFormElementRef.current) {
        if (isMobileModalOpen()) {
          // Reset styles when modal is open
          searchFormElementRef.current.style.marginTop = '0px';
          searchFormElementRef.current.style.transition = 'none';
          searchFormElementRef.current.style.position = '';
          searchFormElementRef.current.style.top = '';
          searchFormElementRef.current.style.left = '';
          searchFormElementRef.current.style.right = '';
          searchFormElementRef.current.style.zIndex = '';
        }
      }
    };

    // Use MutationObserver to watch for modal appearing/disappearing
    if (window.MutationObserver) {
      modalObserver = new MutationObserver(() => {
        checkAndResetStyles();
      });

      modalObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    // Initial check
    checkAndResetStyles();

    return () => {
      if (modalObserver) {
        modalObserver.disconnect();
      }
    };
  }, [enabled]);

  // Calculate initial form position
  useEffect(() => {
    if (!enabled || !searchFormElementRef.current) return;

    const updateFormOffsetTop = () => {
      if (searchFormElementRef.current) {
        // Don't apply margin-top when mobile modal is open
        if (isMobileModalOpen()) {
          return;
        }

        // Set the initial position based on headerHeight + 24px gap from the top
        formInitialOffsetTop.current = initialTopPosition;

        // Apply initial positioning when form is not sticky
        if (!scrollState.isSticky) {
          searchFormElementRef.current.style.marginTop = `${initialTopPosition}px`;
        }
      }
    };

    updateFormOffsetTop();

    // Recalculate on resize
    window.addEventListener('resize', updateFormOffsetTop);
    return () => window.removeEventListener('resize', updateFormOffsetTop);
  }, [enabled, initialTopPosition, scrollState.isSticky, headerHeight]);

  // Main scroll handler
  useEffect(() => {
    if (!enabled) return;

    const handleScroll = () => {
      // Skip scroll handling when mobile modal is open
      if (isMobileModalOpen()) {
        return;
      }

      const currentScrollY = window.scrollY;
      const scrollDiff = currentScrollY - lastScrollY.current;

      // Determine scroll direction
      const direction = scrollDiff > 0 ? 'down' : scrollDiff < 0 ? 'up' : 'idle';

      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // Header visibility logic with buffer zone to prevent fluctuation
      // Add a small buffer (10px) to prevent flickering at the exact boundary
      const headerBuffer = 10;
      const shouldHideHeader = currentScrollY > headerHeight + headerBuffer;

      // Calculate if form should be sticky with the same buffer
      const shouldBeSticky = shouldHideHeader;

      // Apply header styles with synchronized timing.
      // Using margin-top instead of transform intentionally: transform creates a new
      // stacking context and containing block, which traps position:fixed descendants
      // (e.g. Sign-in / My Trips modals) inside the header element, causing them to
      // slide off-screen with the header and appear below page content.
      const headerTransition = 'margin-top 0.3s ease-in-out';

      if (headerElementRef.current) {
        if (shouldHideHeader) {
          // using margin-top instead of translateY
          // because -100% in translateY is 100% of the element's own height
          // which is exactly what element.offsetHeight returns
          // So both shift the header off-screen by the same distance.
          const height = headerElementRef.current.offsetHeight;
          headerElementRef.current.style.marginTop = `-${height}px`;
          headerElementRef.current.style.transition = headerTransition;
        } else {
          headerElementRef.current.style.marginTop = '0px';
          headerElementRef.current.style.transition = headerTransition;
        }
      }

      if (brandBarElementRef.current) {
        if (shouldHideHeader) {
          brandBarElementRef.current.style.display = 'none';
        } else {
          brandBarElementRef.current.style.display = '';
        }
      }

      if (hotelHeaderElementRef.current) {
        if (shouldHideHeader) {
          hotelHeaderElementRef.current.style.display = 'none';
        } else {
          hotelHeaderElementRef.current.style.display = '';
        }
      }

      // Apply search form positioning with smooth transition
      const searchFormHeight = searchFormElementRef.current?.offsetHeight || 0;

      if (searchFormElementRef.current) {
        // Add transition for smooth animation
        searchFormElementRef.current.style.transition = 'transform 0.3s ease-in-out, margin-top 0.3s ease-in-out';

        if (shouldBeSticky && searchFormHeight > 0) {
          // Make form sticky at top
          searchFormElementRef.current.style.marginTop = '0px';
          searchFormElementRef.current.style.position = 'fixed';
          searchFormElementRef.current.style.top = '0px';
          searchFormElementRef.current.style.right = '0px';
          searchFormElementRef.current.style.zIndex = '1000';
        } else {
          // Restore normal positioning
          searchFormElementRef.current.style.marginTop = `${initialTopPosition}px`;
          searchFormElementRef.current.style.position = '';
          searchFormElementRef.current.style.top = '';
          searchFormElementRef.current.style.right = '';
          searchFormElementRef.current.style.zIndex = '';
        }
      }

      setScrollState({
        isSticky: shouldBeSticky,
        hideHeader: shouldHideHeader,
        scrollDirection: direction,
        scrollPosition: currentScrollY,
      });

      lastScrollY.current = currentScrollY;

      // Set idle state after scroll stops
      scrollTimeout.current = setTimeout(() => {
        setScrollState(prev => ({
          ...prev,
          scrollDirection: 'idle',
        }));
      }, 150);
    };

    // Throttle scroll events
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });

    // Initial call to set state
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // Reset header styles and body padding on cleanup
      if (headerElementRef.current) {
        headerElementRef.current.style.marginTop = '';
        headerElementRef.current.style.transition = '';
      }

      if (brandBarElementRef.current) {
        brandBarElementRef.current.style.display = '';
      }

      if (hotelHeaderElementRef.current) {
        hotelHeaderElementRef.current.style.display = '';
      }

      // Reset form styles on cleanup
      if (searchFormElementRef.current) {
        searchFormElementRef.current.style.marginTop = '';
        searchFormElementRef.current.style.transition = '';
        searchFormElementRef.current.style.position = '';
        searchFormElementRef.current.style.top = '';
        searchFormElementRef.current.style.right = '';
        searchFormElementRef.current.style.zIndex = '';
      }
    };
  }, [enabled, initialTopPosition, headerHeight]);

  // Return disabled state when hook is not enabled
  if (!enabled) {
    return {
      scrollState: {
        isSticky: false,
        hideHeader: false,
        scrollDirection: 'idle',
        scrollPosition: 0,
      },
      searchFormRef,
      headerElement: null,
    };
  }

  return {
    scrollState,
    searchFormRef,
    headerElement: headerElementRef.current,
  };
};
